<script setup>
/**
 * SzSortToggle (montado como ConversationBasicFilter pra compat) —
 * botão pill com ícone + label que ALTERNA entre:
 *   - last_activity_at_desc → "Mais recentes" (default)
 *   - last_activity_at_asc  → "Mais antigas"
 *
 * Por que `last_activity_at` e não `last_message_at`:
 *   O backend (conversation_finder.rb SORT_OPTIONS) só conhece
 *   last_activity_at/created_at/priority/waiting_since. Quando recebe
 *   `last_message_at_*` cai no fallback `last_activity_at_desc` e ignora
 *   o asc — então o toggle aparentava funcionar mas não trazia ordem real.
 *
 * Sem dropdown. Demais sort modes (priority, waiting_since, etc.) ficam
 * inacessíveis aqui — trade-off pra UX simples.
 */
import { computed, ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import wootConstants from 'dashboard/constants/globals';

defineProps({
  isOnExpandedLayout: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(['changeFilter']);

const store = useStore();
const { updateUISettings } = useUISettings();

const chatStatusFilter = useMapGetter('getChatStatusFilter');
const chatSortFilter = useMapGetter('getChatSortFilter');

// Aceita valores legados (last_message_at_*) e os normaliza pra last_activity_at_*
// Isso protege quem tem o setting antigo salvo em UI settings.
function normalizeSort(value) {
  if (value === 'last_message_at_asc') return 'last_activity_at_asc';
  if (value === 'last_message_at_desc') return 'last_activity_at_desc';
  return value || 'last_activity_at_desc';
}

const currentSort = computed(() => normalizeSort(chatSortFilter.value));
const isDesc = computed(() => currentSort.value === 'last_activity_at_desc');
const sortLabel = computed(() =>
  isDesc.value ? 'Mais recentes' : 'Mais antigas'
);

function toggleSort() {
  const next = isDesc.value
    ? 'last_activity_at_asc'
    : 'last_activity_at_desc';
  emit('changeFilter', next, 'sort');
  store.dispatch('setChatSortFilter', next);
  updateUISettings({
    conversations_filter_by: {
      status: chatStatusFilter.value || wootConstants.STATUS_TYPE.OPEN,
      order_by: next,
    },
  });
}

// ── Compact mode dinâmico ─────────────────────────────────────────────
// Estratégia: sempre tenta render expandido (com label); se o header
// faz overflow horizontal (scrollWidth > clientWidth), vira compact.
// Auto-adaptativo a zoom/monitor/idioma. Sem números mágicos.
const sortBtnEl = ref(null);
const isCompact = ref(false);
let parentEl = null;
let ro = null;
let measuring = false;

async function measureFit() {
  if (measuring) return;
  if (!parentEl || !sortBtnEl.value) return;
  measuring = true;
  try {
    // Tenta render expandido (label visível)
    isCompact.value = false;
    await nextTick();
    // Se header overflowa, comprime. +1 tolerância pra sub-pixel rounding.
    if (parentEl.scrollWidth > parentEl.clientWidth + 1) {
      isCompact.value = true;
    }
  } finally {
    measuring = false;
  }
}

onMounted(async () => {
  await nextTick();
  if (!sortBtnEl.value) return;
  parentEl = sortBtnEl.value.closest('.sz-chl-header');
  if (!parentEl) return;
  ro = new ResizeObserver(() => measureFit());
  ro.observe(parentEl);
  measureFit();
});

onBeforeUnmount(() => {
  if (ro) { ro.disconnect(); ro = null; }
});
</script>

<template>
  <div
    ref="sortBtnEl"
    v-tooltip.top="sortLabel"
    class="sz-sortbtn"
    :class="{ 'sz-sortbtn--compact': isCompact }"
    role="button"
    tabindex="0"
    :aria-label="sortLabel"
    @click="toggleSort"
    @keydown.enter.prevent="toggleSort"
    @keydown.space.prevent="toggleSort"
  >
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path d="m21 16-4 4-4-4" />
      <path d="M17 20V4" />
      <path d="m3 8 4-4 4 4" />
      <path d="M7 4v16" />
    </svg>
    <span class="sz-sortbtn-label">{{ sortLabel }}</span>
  </div>
</template>

<style scoped>
/* Botão pill — ícone + label da ordenação ativa (Figma).
   flex-shrink: 1 + min-width: 0 permitem o filho .sz-sortbtn-label comprimir
   antes de fazer overflow no parent. */
.sz-sortbtn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 4px 8px 4px 4px;
  background: #FFFFFF;
  border: 1.5px solid #E5E5E5;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  box-sizing: border-box;
  color: #3F3F3F;
  flex: none;
  transition: background 0.15s, border-color 0.15s;
}
.sz-sortbtn:hover { background: rgba(0, 0, 0, 0.03); }
.sz-sortbtn:focus-visible {
  outline: 2px solid #388E3C;
  outline-offset: 1px;
}
/* Label cresce até caber e trunca progressivamente com ellipsis conforme
   a pressão do flex parent — ex.: 125% zoom → "Mais recen…". */
.sz-sortbtn-label {
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  color: inherit;
  white-space: nowrap;
  flex: none;
}

/* Cutoff final: em viewports realmente pequenos (≈150% zoom em FHD), some o label
   e o botão vira só ícone 32x32. Tooltip via aria-label preserva o significado. */
.sz-sortbtn--compact .sz-sortbtn-label { display: none; }
.sz-sortbtn--compact {
  width: 32px;
  padding: 4px;
  justify-content: center;
  gap: 0;
}

/* Dark mode (Figma) */
.dark .sz-sortbtn {
  background: #1D1E21;
  border-color: #303138;
  color: #FFFFFF;
}
.dark .sz-sortbtn:hover { background: #26282B; }
.dark .sz-sortbtn-label { color: #FFFFFF; }
</style>
